import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, map, of, Subscription, tap } from 'rxjs';

const REQUESTED_ROUTE = 'requestedRouteBeforeAuth';
@Injectable()
export class AuthService {
  oidcSecurityService = inject(OidcSecurityService);
  private router = inject(Router);

  isAuthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(
    map((res: { isAuthenticated: boolean }) => res.isAuthenticated)
  );
  private checkAuthSub?: Subscription;
  destroyRef = inject(DestroyRef);

  // Nessy Explorer is its own page at /explorer/ and sends a visitor without a sign-in here, saying
  // where to go back to. Read before the router redirects, and only an Explorer path, so it cannot
  // send anyone off the site.
  private explorerReturn = (() => {
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');

    return returnTo?.startsWith('/explorer/') ? returnTo : null;
  })();

  login() {
    this.checkAuthSub = this.oidcSecurityService
      .checkAuth()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((res) => {
          if (res.isAuthenticated) {
            const requestedRoute = this.explorerReturn ?? localStorage.getItem(REQUESTED_ROUTE);

            if (requestedRoute) {
              localStorage.removeItem(REQUESTED_ROUTE);

              // not an Angular route - the router would send it to the ** fallback
              if (requestedRoute.startsWith('/explorer/')) window.location.replace(requestedRoute);
              else this.router.navigateByUrl(requestedRoute);
            }
          } else {
            if (res.errorMessage?.startsWith('Error: authorizedCallback, token(s) validation failed')) {
              window.location.assign('https://id.nessy.com/diagnostics/customerdebug');
            } else {
              this.goToIdentityLoginPage();
            }
          }
        }),
        catchError(() => {
          //fallback for some token issues
          this.oidcSecurityService.logoffLocal();
          this.goToIdentityLoginPage();
          return of(null);
        })
      )
      .subscribe();
  }

  goToIdentityLoginPage() {
    localStorage.setItem(REQUESTED_ROUTE, this.explorerReturn ?? window.location.pathname), this.oidcSecurityService.authorize();
  }

  bearerToken() {
    return this.oidcSecurityService.getAccessToken();
  }

  destroy() {
    this.checkAuthSub && this.checkAuthSub.unsubscribe();
  }

  logout() {
    this.destroy();
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
  }
}
